<script setup lang="ts">
import CheckboxFive from '@/components/Forms/Checkboxes/CheckboxFive.vue'
import DashboardPdfConfirmModal from '@/components/Dashboard/Modals/DashboardPdfConfirmModal.vue'
import IcAnimateSpin from '@/components/Icons/IcAnimateSpin.vue'
import IcDownload from '@/components/Icons/IcDownload.vue'
import { useDashboardStore } from '@/store/dashboard'
import { useGroupStore } from '@/store/group'
import { storeToRefs } from 'pinia'
import { computed, provide, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import DashboardSearchBar from './DashboardSearchBar.vue'

const { t } = useI18n()

const dashboardStore = useDashboardStore()
const { getLastSearchType, canGenerateReport, isGeneratingReport, visibleSearches } = storeToRefs(dashboardStore)
const { fetch: fetchUserGroups } = useGroupStore()

const selectedCheckbox = ref<'group' | 'sensor' | ''>(getLastSearchType.value || 'sensor')
const isPdfConfirmOpen = ref(false)

const visibleSearchLabels = computed(() => visibleSearches.value.map((s) => s.label))

provide('selectedCheckbox', selectedCheckbox)

watch(selectedCheckbox, (newValue) => {
  dashboardStore.setLastSearchType(newValue)

  if (newValue === 'sensor') {
    dashboardStore.fetchSensors()
  } else if (newValue === 'group') {
    fetchUserGroups()
  }
})

watch(
  getLastSearchType,
  (newValue) => {
    if (newValue !== selectedCheckbox.value) {
      selectedCheckbox.value = newValue
    }
  },
  { immediate: true },
)

function openPdfConfirm() {
  if (!canGenerateReport.value || isGeneratingReport.value) return
  isPdfConfirmOpen.value = true
}

function closePdfConfirm() {
  isPdfConfirmOpen.value = false
}

async function confirmGeneratePdf() {
  closePdfConfirm()
  await dashboardStore.generateReport()
}
</script>

<template>
  <div class="flex min-w-0 w-full flex-col p-6 lg:px-8">
    <div class="mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between">
      <div class="mb-4 flex max-w-lg flex-col sm:mb-0 sm:mr-4 sm:max-w-none">
        <div class="flex flex-wrap items-center gap-4 pb-4">
          <h1 class="text-title-md2 font-semibold text-black">
            {{ t('dashboard.header.title') }}
          </h1>
          <div class="flex flex-wrap items-center gap-4">
            <CheckboxFive labelText="Sensores" value="sensor" />
            <CheckboxFive labelText="Grupos" value="group" />
          </div>
        </div>
        <p class="max-w-prose text-left text-sm font-medium leading-relaxed text-body">
          {{ t('dashboard.header.subtitle_workspace') }}
        </p>
      </div>
      <div class="hidden shrink-0 items-center justify-center xl:flex">
        <button
          type="button"
          :disabled="!canGenerateReport || isGeneratingReport"
          class="min-w-45 flex items-center justify-center gap-2 rounded-none bg-green-dark px-4 py-3 text-white hover:bg-emerald-600 disabled:cursor-not-allowed disabled:opacity-60"
          @click.prevent="openPdfConfirm">
          <IcDownload v-if="!isGeneratingReport" class="h-5 w-5 text-white" />
          <span v-else class="animate-spin">
            <IcAnimateSpin class="h-5 w-5 text-white" />
          </span>
          <span>
            {{
              isGeneratingReport
                ? t('dashboard.header.buttons.generating_pdf')
                : t('dashboard.header.buttons.generate_pdf')
            }}
          </span>
        </button>
      </div>
    </div>

    <DashboardSearchBar />

    <DashboardPdfConfirmModal
      :is-open="isPdfConfirmOpen"
      :search-labels="visibleSearchLabels"
      :search-count="visibleSearches.length"
      @close="closePdfConfirm"
      @confirm="confirmGeneratePdf" />
  </div>
</template>
